import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  buildOptimisticPostComment,
  type OptimisticCommentAuthor,
  serverCommentMatchesOptimisticComment,
} from '@/src/features/comments/optimistic-comment';
import { usePendingComments } from '@/src/features/offline/use-outbox';
import {
  loadCachedComments,
  saveCachedComments,
} from '@/src/features/posts/feed-cache';
import {
  createPostComment,
  deletePostComment,
  editPostComment,
  PUBLIC_POST_LIMIT,
  subscribeToPostCommentMutations,
  subscribeToPostComments,
  type CreatePostCommentInput,
  type PostComment,
} from './post-service';

type PostCommentsState = {
  comments: PostComment[];
  error: Error | null;
  loading: boolean;
};

type CreatePostCommentMutationInput = CreatePostCommentInput &
  OptimisticCommentAuthor;

const optimisticCommentsByScopeKey = new Map<string, PostComment[]>();
const optimisticCommentListenersByScopeKey = new Map<string, Set<() => void>>();
const commentRefreshListenersByPostId = new Map<string, Set<() => void>>();

function optimisticPostCommentScopeKey(
  postId: string | undefined,
  uid: string | undefined,
) {
  return postId && uid ? `${postId}:${uid}` : null;
}

function getOptimisticPostComments(
  postId: string | undefined,
  uid: string | undefined,
) {
  const scopeKey = optimisticPostCommentScopeKey(postId, uid);
  return scopeKey ? (optimisticCommentsByScopeKey.get(scopeKey) ?? []) : [];
}

function notifyOptimisticPostCommentListeners(scopeKey: string) {
  optimisticCommentListenersByScopeKey
    .get(scopeKey)
    ?.forEach((listener) => listener());
}

function setOptimisticPostComments(
  postId: string,
  uid: string,
  updater: (comments: PostComment[]) => PostComment[],
) {
  const scopeKey = optimisticPostCommentScopeKey(postId, uid);

  if (!scopeKey) {
    return;
  }

  const nextComments = updater(optimisticCommentsByScopeKey.get(scopeKey) ?? []);

  if (nextComments.length > 0) {
    optimisticCommentsByScopeKey.set(scopeKey, nextComments);
  } else {
    optimisticCommentsByScopeKey.delete(scopeKey);
  }

  notifyOptimisticPostCommentListeners(scopeKey);
}

function subscribeToOptimisticPostComments(
  postId: string,
  uid: string,
  listener: () => void,
) {
  const scopeKey = optimisticPostCommentScopeKey(postId, uid);

  if (!scopeKey) {
    return () => {};
  }

  const listeners =
    optimisticCommentListenersByScopeKey.get(scopeKey) ?? new Set<() => void>();
  listeners.add(listener);
  optimisticCommentListenersByScopeKey.set(scopeKey, listeners);

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0) {
      optimisticCommentListenersByScopeKey.delete(scopeKey);
    }
  };
}

function notifyPostCommentRefreshListeners(postId: string) {
  commentRefreshListenersByPostId.get(postId)?.forEach((listener) => {
    listener();
  });
}

function subscribeToPostCommentRefreshRequests(
  postId: string,
  listener: () => void,
) {
  const listeners =
    commentRefreshListenersByPostId.get(postId) ?? new Set<() => void>();
  listeners.add(listener);
  commentRefreshListenersByPostId.set(postId, listeners);

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0) {
      commentRefreshListenersByPostId.delete(postId);
    }
  };
}

function addOptimisticPostComment(input: CreatePostCommentMutationInput) {
  const optimisticComment = buildOptimisticPostComment(input);

  setOptimisticPostComments(input.postId, input.authorUid, (comments) => [
    optimisticComment,
    ...comments,
  ]);

  return optimisticComment.id;
}

function removeOptimisticPostComment(
  postId: string,
  uid: string,
  commentId: string,
) {
  setOptimisticPostComments(postId, uid, (comments) =>
    comments.filter((comment) => comment.id !== commentId),
  );
}

function reconcileOptimisticPostComments(
  postId: string,
  uid: string,
  serverComments: PostComment[],
) {
  setOptimisticPostComments(postId, uid, (comments) =>
    comments.filter(
      (optimistic) =>
        !serverComments.some((server) =>
          serverCommentMatchesOptimisticComment({ optimistic, server }),
        ),
    ),
  );
}

function mergeUniqueComments(...commentGroups: PostComment[][]) {
  const seenIds = new Set<string>();
  const comments: PostComment[] = [];

  commentGroups.flat().forEach((comment) => {
    if (seenIds.has(comment.id)) {
      return;
    }

    seenIds.add(comment.id);
    comments.push(comment);
  });

  return comments;
}

export function usePostComments(
  postId: string | undefined,
  uid?: string | undefined,
  limitCount = 50,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const [effectiveLimit, setEffectiveLimit] = useState(limitCount);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const commentsRef = useRef<PostComment[]>([]);
  const postIdRef = useRef<string | undefined>(undefined);
  const pendingComments = usePendingComments({ postId, uid });
  const [optimisticComments, setOptimisticComments] = useState<PostComment[]>(
    () => getOptimisticPostComments(postId, uid),
  );
  const [state, setState] = useState<PostCommentsState>({
    comments: [],
    error: null,
    loading: Boolean(postId),
  });

  const refresh = useCallback(() => {
    if (!postId || !enabled) {
      return;
    }

    setRefreshing(true);
    setRefreshNonce((currentNonce) => currentNonce + 1);
  }, [enabled, postId]);

  useEffect(() => {
    if (!postId || !uid) {
      setOptimisticComments([]);
      return undefined;
    }

    setOptimisticComments(getOptimisticPostComments(postId, uid));

    return subscribeToOptimisticPostComments(postId, uid, () => {
      setOptimisticComments(getOptimisticPostComments(postId, uid));
    });
  }, [postId, uid]);

  useEffect(() => {
    if (!postId || !enabled) {
      return undefined;
    }

    return subscribeToPostCommentRefreshRequests(postId, refresh);
  }, [enabled, postId, refresh]);

  useEffect(() => {
    if (!postId || !enabled) {
      return undefined;
    }

    return subscribeToPostCommentMutations({
      postId,
      onError: () => undefined,
      onNext: refresh,
    });
  }, [enabled, postId, refresh]);

  useEffect(() => {
    setEffectiveLimit(limitCount);
  }, [limitCount]);

  useEffect(() => {
    if (postIdRef.current !== postId) {
      postIdRef.current = postId;
      commentsRef.current = [];
    }

    if (!postId || !enabled) {
      commentsRef.current = [];
      setLoadingMore(false);
      setRefreshing(false);
      setState({ comments: [], error: null, loading: false });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: commentsRef.current.length === 0,
      comments: commentsRef.current.length === 0 ? [] : currentState.comments,
    }));

    if (commentsRef.current.length === 0) {
      void loadCachedComments(postId).then((cachedComments) => {
        if (cachedComments.length > 0) {
          commentsRef.current = cachedComments;
          setState((currentState) => ({
            ...currentState,
            comments: cachedComments,
            loading: false,
          }));
        }
      });
    }

    return subscribeToPostComments({
      limitCount: effectiveLimit,
      postId,
      onError: (error) => {
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
        setLoadingMore(false);
        setRefreshing(false);
      },
      onNext: (comments) => {
        if (uid) {
          reconcileOptimisticPostComments(postId, uid, comments);
        }
        commentsRef.current = comments;
        setState({ comments, error: null, loading: false });
        setLoadingMore(false);
        setRefreshing(false);
        void saveCachedComments(postId, comments);
      },
    });
  }, [effectiveLimit, enabled, postId, refreshNonce, uid]);

  const hasMore = state.comments.length >= effectiveLimit;

  const loadMore = useCallback(() => {
    if (state.loading || loadingMore || refreshing || !hasMore) {
      return;
    }

    setLoadingMore(true);
    setEffectiveLimit((currentLimit) => currentLimit + PUBLIC_POST_LIMIT);
  }, [hasMore, loadingMore, refreshing, state.loading]);

  return {
    ...state,
    comments: mergeUniqueComments(
      pendingComments,
      optimisticComments,
      state.comments,
    ),
    hasMore,
    loadMore,
    loadingMore,
    refresh,
    refreshing,
  };
}

export function useCreatePostComment() {
  return useMutation<void, Error, CreatePostCommentMutationInput, {
    optimisticCommentId: string;
    authorUid: string;
    postId: string;
  }>({
    mutationFn: (input) => createPostComment(input),
    onMutate: (input) => {
      return {
        authorUid: input.authorUid,
        optimisticCommentId: addOptimisticPostComment(input),
        postId: input.postId,
      };
    },
    onError: (_error, _input, context) => {
      if (context) {
        removeOptimisticPostComment(
          context.postId,
          context.authorUid,
          context.optimisticCommentId,
        );
      }
    },
    onSuccess: (_result, _input, context) => {
      if (!context) {
        return;
      }

      notifyPostCommentRefreshListeners(context.postId);
    },
  });
}

export function useDeletePostComment() {
  return useMutation({
    mutationFn: deletePostComment,
    onSuccess: (_result, input) => {
      notifyPostCommentRefreshListeners(input.postId);
    },
  });
}

export function useEditPostComment() {
  return useMutation({
    mutationFn: editPostComment,
    onSuccess: (_result, input) => {
      notifyPostCommentRefreshListeners(input.postId);
    },
  });
}
